import {
  DEMO_TRANSACTIONS_LEGACY,
  FORECAST_90_DAY_ENDPOINT,
} from "./demo-data";
import { BANK_ACCOUNTS } from "./financial-systems";
import { getDemoForecastTransactionRows } from "./forecastTransactionsDemoData";

/**
 * Demo-mode data for the three 12-month forecast views.
 *
 * In live mode each view is backed by its own GraphQL reporting view
 * (`vw_Forecast12Month{Balances,CategoryBalances,Transactions}`). There is no
 * backend in demo mode, so equivalently-shaped rows are derived from the
 * in-memory BuildRight demo data. All filtering / sorting / paging is then
 * handled client-side by MaximusTableReusable.
 */

const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/** A single month of the demo 12-month base forecast. */
export interface Demo12MonthPoint {
  /** Display label, e.g. "Mar 2026". */
  month: string;
  /** Server-equivalent `MonthKey`, e.g. "2026-03". */
  monthKey: string;
  balance: number;
  inflow: number;
  outflow: number;
  net: number;
}

/** `MonthKey` ("yyyy-MM") for a date, matching the SQL views. */
export function monthKeyOf(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

/** Display label ("MMM yyyy") for a `MonthKey`. */
export function monthKeyLabel(monthKey: string | null | undefined): string {
  if (!monthKey) return "—";
  const [year, month] = monthKey.split("-").map(Number);
  if (!year || !month || month < 1 || month > 12) return monthKey;
  return `${MONTH_NAMES[month - 1]} ${year}`;
}

/** The demo 12-month base forecast, starting at the current month. */
export function generate12MonthData(): Demo12MonthPoint[] {
  const balances = [
    197000,
    145000,
    FORECAST_90_DAY_ENDPOINT,
    95000,
    118000,
    135000,
    122000,
    108000,
    98000,
    125000,
    150000,
    172000,
  ];
  const inflows = [
    140000, 138000, 115000, 145000, 165000, 155000, 145000, 125000, 118000,
    115000, 145000, 165000,
  ];
  const outflows = [
    190000, 190000, 189700, 120000, 140000, 138000, 128000, 138000, 132000,
    128000, 118000, 118000,
  ];

  const today = new Date();
  return balances.map((balance, idx) => {
    const date = new Date(today.getFullYear(), today.getMonth() + idx, 1);
    return {
      month: `${MONTH_NAMES[date.getMonth()]} ${date.getFullYear()}`,
      monthKey: monthKeyOf(date),
      balance,
      inflow: inflows[idx],
      outflow: outflows[idx],
      net: inflows[idx] - outflows[idx],
    };
  });
}

/** Row shape shared by the three demo 12-month views. */
export interface Forecast12MonthDemoRow {
  rowKey: string;
  id: number;
  monthKey?: string | null;
  // balances
  date?: string | null;
  openingBalance?: number | null;
  closingBalance?: number | null;
  netChange?: number | null;
  // category balances
  category?: string | null;
  transactionCount?: number | null;
  averageConfidenceScore?: number | null;
  // shared money columns
  incoming?: number | null;
  outgoing?: number | null;
  confidenceScore?: number | null;
  // transactions
  forecastDate?: string | null;
  dueDate?: string | null;
  counterpartyName?: string | null;
  description?: string | null;
  direction?: string | null;
  type?: string | null;
  grossAmount?: number | null;
  outstandingAmount?: number | null;
  layer?: string | null;
  status?: string | null;
  reference?: string | null;
  dataSourceName?: string | null;
}

/** Demo monthly balances — opening rolls forward from the previous close. */
export function getDemo12MonthBalanceRows(): Forecast12MonthDemoRow[] {
  const months = generate12MonthData();
  const startingCash = BANK_ACCOUNTS.reduce((sum, a) => sum + a.balance, 0);
  return months.map((point, index) => ({
    rowKey: `demo-12m-bal-${point.monthKey}`,
    id: index + 1,
    monthKey: point.monthKey,
    date: `${point.monthKey}-01`,
    openingBalance: index === 0 ? startingCash : months[index - 1].balance,
    closingBalance: point.balance,
    incoming: point.inflow,
    outgoing: point.outflow,
    netChange: point.net,
    confidenceScore: 85,
  }));
}

/**
 * Demo category balances. Each month's incoming/outgoing totals are split
 * across the demo ledger's categories in the same proportions the legacy
 * transactions use, so the category rows always reconcile to their month.
 */
export function getDemo12MonthCategoryRows(): Forecast12MonthDemoRow[] {
  const expenses = DEMO_TRANSACTIONS_LEGACY.filter((t) => t.type === "expense");
  const totalExpense = expenses.reduce((sum, t) => sum + Math.abs(t.amount), 0);
  const expenseByCategory = expenses.reduce<
    Record<string, { amount: number; count: number }>
  >((acc, txn) => {
    const key = txn.category as string;
    const existing = acc[key] ?? { amount: 0, count: 0 };
    existing.amount += Math.abs(txn.amount);
    existing.count += 1;
    acc[key] = existing;
    return acc;
  }, {});

  const rows: Forecast12MonthDemoRow[] = [];
  let id = 0;
  for (const point of generate12MonthData()) {
    const receipts = Math.round(point.inflow * 0.9);
    rows.push({
      rowKey: `demo-12m-cat-${point.monthKey}-receipts`,
      id: ++id,
      monthKey: point.monthKey,
      category: "Customer Receipts",
      incoming: receipts,
      outgoing: 0,
      netChange: receipts,
      transactionCount: 12,
      averageConfidenceScore: 90,
    });
    rows.push({
      rowKey: `demo-12m-cat-${point.monthKey}-other-income`,
      id: ++id,
      monthKey: point.monthKey,
      category: "Other Income",
      incoming: point.inflow - receipts,
      outgoing: 0,
      netChange: point.inflow - receipts,
      transactionCount: 3,
      averageConfidenceScore: 70,
    });
    const categories = Object.entries(expenseByCategory);
    let allocated = 0;
    categories.forEach(([category, { amount, count }], index) => {
      // The final category absorbs the rounding remainder so the month's category
      // outgoings sum exactly to the balance row's outgoing, as in live mode.
      const outgoing =
        index === categories.length - 1
          ? point.outflow - allocated
          : totalExpense > 0
            ? Math.round((amount / totalExpense) * point.outflow)
            : 0;
      allocated += outgoing;
      rows.push({
        rowKey: `demo-12m-cat-${point.monthKey}-${category}`,
        id: ++id,
        monthKey: point.monthKey,
        category,
        incoming: 0,
        outgoing,
        netChange: -outgoing,
        transactionCount: count,
        averageConfidenceScore: 80,
      });
    });
  }
  return rows;
}

/** Demo 12-month transactions — the forecast transactions plus their month. */
export function getDemo12MonthTransactionRows(): Forecast12MonthDemoRow[] {
  return getDemoForecastTransactionRows().map((row) => ({
    rowKey: row.rowKey,
    id: row.id,
    monthKey: row.forecastDate ? monthKeyOf(new Date(row.forecastDate)) : null,
    forecastDate: row.forecastDate,
    dueDate: row.dueDate,
    counterpartyName: row.counterpartyName,
    description: row.description,
    category: row.category,
    direction: row.direction,
    type: row.type,
    grossAmount: row.grossAmount,
    outstandingAmount: row.outstandingAmount,
    layer: row.layer,
    status: row.status,
    reference: row.reference,
    dataSourceName: row.dataSourceName,
  }));
}

function distinctSorted(values: (string | null | undefined)[]): string[] {
  return Array.from(
    new Set(
      values.filter((v): v is string => typeof v === "string" && v.length > 0),
    ),
  ).sort((a, b) => a.localeCompare(b));
}

/** Distinct dropdown options for the demo category balances view. */
export function getDemo12MonthCategoryOptions(): Record<string, string[]> {
  return {
    Category: distinctSorted(
      getDemo12MonthCategoryRows().map((r) => r.category),
    ),
  };
}

/** Distinct dropdown options for the demo 12-month transactions view. */
export function getDemo12MonthTransactionOptions(): Record<string, string[]> {
  const rows = getDemo12MonthTransactionRows();
  return {
    Category: distinctSorted(rows.map((r) => r.category)),
    Direction: distinctSorted(rows.map((r) => r.direction)),
    Type: distinctSorted(rows.map((r) => r.type)),
    Layer: distinctSorted(rows.map((r) => r.layer)),
    Status: distinctSorted(rows.map((r) => r.status)),
    DataSourceName: distinctSorted(rows.map((r) => r.dataSourceName)),
  };
}
